<template>
  <hypto-popup title="Create UPI">
    <template v-slot:body>
      <div class="merge-fields">
        <md-field>
          <label>UPI ID Bank</label>
          <div>
            <md-radio v-model="upiSuffix" :value="suffixOptions.yesb">Yesbank</md-radio>
            <md-radio v-model="upiSuffix" :value="suffixOptions.icic">ICICI</md-radio>
          </div>
        </md-field>

        <div class="prefix-hypto" v-if="singleUpiPrefix && upiSuffix !== suffixOptions.icic">
          <p>Remove the prefix 'hypto'</p>
          <md-checkbox v-model="removePrefixHypto" />
        </div>

        <md-field :class="{'input-error': $v.upiId.$error || formErrors.upi_id.length, 'no-border': upiPrefix, 'no-right-border': !upiPrefix}">
          <label>UPI ID</label>
          <div class="custom-field">
            <span class="input-prefix" v-if="upiPrefix">
              <h6>{{upiPrefix}}</h6>
            </span>
            <md-input v-model="upiId" @blur="$v.upiId.$touch()" :ref="'UpiId'" />
            <span class="input-suffix">
              <h6>{{upiSuffix}}</h6>
            </span>
          </div>
        </md-field>
        <h6 class="error-message" v-if="$v.upiId.$error && !$v.upiId.required && !singleUpiPrefix">Please enter UPI ID</h6>
        <h6 class="error-message" v-else-if="$v.upiId.$error && !$v.upiId.numeric && singleUpiPrefix">Please enter only numbers</h6>
        <h6 class="error-message" v-else-if="$v.upiId.$error && !$v.upiId.vpaPrefixNum && !singleUpiPrefix && (/^[a-zA-Z]+[0-9]*$/).test(upiId)">Please provide only lowercase alphabets for prefix</h6>
        <h6 class="error-message" v-else-if="$v.upiId.$error && !$v.upiId.vpaPrefixNum && !singleUpiPrefix">Please provide a valid upi_id.</h6>
        <h6 class="error-message" v-else-if="$v.upiId.$error && !$v.upiId.icicUpiLength">The text between 'hypto.' and '@icici' should contain 2-20 characters</h6>
        <h6 class="error-message" v-else-if="$v.upiId.$error && !$v.upiId.yesbUpiLength">The text before @yesbank should contain 2-100 characters</h6>
        <error-info v-else :errorList="formErrors.upi_id" />

        <md-field :class="{'input-error': $v.name.$error || formErrors.name.length}">
          <label>Name <i>(Max 40 characters)</i></label>
          <md-input v-model="name" @blur="$v.name.$touch()" :maxlength="40" />
        </md-field>
        <h6 class="error-message" v-if="$v.name.$error && !$v.name.alphaNumericSpace">Only alphanumeric characters and space are allowed</h6>
        <h6 class="error-message" v-else-if="$v.name.$error">Please enter UPI Name</h6>
        <error-info v-else :errorList="formErrors.name" />

        <md-field :class="{'input-error': $v.pan.$error || formErrors.pan.length}">
          <label>PAN</label>
          <md-input v-model="pan" @blur="$v.pan.$touch()" :maxlength="10" />
        </md-field>
        <h6 class="error-message" v-if="$v.pan.$error && !$v.pan.required">Please enter PAN</h6>
        <h6 class="error-message" v-else-if="$v.pan.$error && !$v.pan.validPan">Please enter valid PAN</h6>
        <error-info v-else :errorList="formErrors.pan" />

        <md-field id="Category" :class="{'input-error': formErrors.category.length}">
          <label>Category</label>
          <md-select v-model="category" @md-opened="setDropdownPosOffset('Category')">
            <md-option v-for="(data, index) in categoryOptions" :key="index" :value="data">
              {{data}}
            </md-option>
          </md-select>
        </md-field>
        <error-info :errorList="formErrors.category" />

        <md-field>
          <label>Channel</label>
          <div>
            <md-radio v-model="channel" :value="'ONLINE'">Online</md-radio>
            <md-radio v-model="channel" :value="'OFFLINE'">Offline</md-radio>
          </div>
        </md-field>

        <md-field :class="{'input-error': $v.address.$error || formErrors.address.length}">
          <label>Address</label>
          <md-textarea v-model="address" @blur="$v.address.$touch()" />
        </md-field>
        <h6 class="error-message" v-if="$v.address.$error">Please enter the Address</h6>
        <error-info v-else :errorList="formErrors.address" />
      </div>
    </template>

    <template v-slot:footer>
      <error-info :errorList="formErrors.upi" />
      <button class="primary-btn align-r" @click="create" :disabled="isDisableButton">Create UPI</button>
    </template>
  </hypto-popup>
</template>
<script>
import HyptoPopup from '@/components/global/HyptoPopup'
import ErrorInfo from '@/components/global/ErrorInfo'

import ApiServices from '@/services/api/upi'
import { required, numeric } from 'vuelidate/lib/validators'
import { validPan, vpaPrefixNum, alphaNumericSpace } from '@/utils/helpers/validators'
import { setDropdownPosOffset, checkFormErrors } from '@/utils/helpers'
import formMixins from '@/mixins/formMixins'

const yesbUpiLength = (prefix) => (upiId) => {
  const total = prefix.length + upiId.length
  return total >= 2 && total <= 100
}

const icicUpiLength = (prefix, bank) => (upiId) => {
  if (bank === '@icici') {
    let vpa = prefix + upiId
    const total = vpa.replace('hypto.', '').length
    return total >= 2 && total <= 20
  }
  return true
}

export default {
  name: 'CreateUpi',
  components: { HyptoPopup, ErrorInfo },
  mixins: [formMixins],
  props: {
    prefixData: Object
  },
  data () {
    return {
      removePrefixHypto: false,
      upiPrefix: '',
      suffixOptions: {
        yesb: '@yesbank',
        icic: '@icici'
      },
      upiSuffix: '@yesbank',
      channel: 'ONLINE',
      singleUpiPrefix: false,
      upiId: '',
      name: null,
      pan: null,
      category: 'Others',
      categoryOptions: [
        'Clothing', 'Restaurants', 'Saloons', 'Groceries', 'Supermarkets', 'Retail Stores', 'Services', 'Others'
      ],
      address: null,
      formErrors: {
        upi_id: [],
        name: [],
        pan: [],
        category: [],
        address: [],
        upi: []
      }
    }
  },
  validations () {
    let valid = {
      name: {
        required,
        alphaNumericSpace
      },
      pan: {
        required,
        validPan
      },
      address: {required}
    }

    if (this.prefixData.approved_prefix_count === 1) {
      valid['upiId'] = {
        numeric,
        yesbUpiLength: yesbUpiLength(this.upiPrefix),
        icicUpiLength: icicUpiLength(this.upiPrefix, this.upiSuffix)
      }
    } else {
      valid['upiId'] = {
        required,
        vpaPrefixNum,
        yesbUpiLength: yesbUpiLength(this.upiPrefix),
        icicUpiLength: icicUpiLength(this.upiPrefix, this.upiSuffix)
      }
    }
    return valid
  },
  created () {
    this.singleUpiPrefix = this.prefixData.approved_prefix_count === 1
  },
  mounted () {
    this.focusUPI()
  },
  watch: {
    removePrefixHypto: {
      handler (newVal) {
        this.formErrors.upi_id = []
        this.appendPrefix()
      },
      immediate: true
    },
    upiSuffix () {
      this.formErrors.upi_id = []
      this.formErrors.upi = []
      this.appendPrefix()
      this.focusUPI()
    },
    pan () {
      this.pan = this.pan.toUpperCase()
      this.formErrors.pan = []
      this.formErrors.upi = []
    },
    upiId () {
      this.formErrors.upi_id = []
      this.formErrors.upi = []
    },
    name () {
      this.formErrors.name = []
      this.formErrors.upi = []
    },
    category () {
      this.formErrors.category = []
      this.formErrors.upi = []
    },
    address () {
      this.formErrors.address = []
      this.formErrors.upi = []
    }
  },
  methods: {
    setDropdownPosOffset,
    checkFormErrors,
    create () {
      this.$v.$touch()
      if (!this.$v.$error && !this.checkFormErrors(this.formErrors)) {
        let params = {
          upi_id: this.upiPrefix + this.upiId + this.upiSuffix,
          name: this.name,
          pan: this.pan,
          category: this.category,
          address: this.address,
          channel: this.channel
        }

        ApiServices.createUpi(params).then((response) => {
          if (response.success) {
            this.successToastMsg(response.message)
            this.$emit('refreshList')
          } else {
            this.errorToastMsg(response.message)
          }

          this.enableButton()
        }).catch((errorData) => {
          let data = errorData.body
          if (typeof data.reason === 'object') {
            this.formErrors = {
              ...this.formErrors,
              ...data.reason
            }
          } else {
            this.formErrors.upi = [data.reason || data.message]
          }
        })
      }
    },
    appendPrefix () {
      const iciciUPI = this.upiSuffix === this.suffixOptions.icic
      if (this.prefixData.approved_prefix_count === 1) {
        if (iciciUPI) {
          this.upiPrefix = 'hypto.'
        } else {
          this.upiPrefix = this.removePrefixHypto ? '' : 'hypto'
        }
        this.upiPrefix += this.prefixData.prefix
      } else {
        this.upiPrefix = iciciUPI ? 'hypto.' : ''
      }
    },
    focusUPI () {
      this.$refs.UpiId.$el.focus()
    }
  }
}
</script>

<style scoped lang="scss">
  @import '@/assets/scss/customField.scss';

  .prefix-hypto {
    @include custom-flex('');

    p {
      @include normal-text($secondary, 14px, 500);
      margin-right: 10px;
    }
  }

  label {
    i {
      color: $grey-text;
      font-weight: 400;
    }
  }

  .mobile {
    .upi-id {
      overflow: auto;
    }
  }

  .md-checkbox {
    margin: 0 !important;
  }
</style>
